import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useMemo,
  useState
} from 'react';
import { Box, HStack, VStack, Text, Grid, Button } from '@chakra-ui/react';
import { FormattedPosition } from '@app/types/formatted-position';
import { Bound, Pool } from '@cryptoalgebra/kim-sdk';
import { Pool as GraphPool } from 'src/types/pool';
import { CollectFeesModalV3 } from '@app/screens/positions/components/CollectFeesModalV3';
import { IDerivedMintInfo } from '@app/state/mintStore';
import { TokenIcon } from '@app/components/TokenIcon';
import { toOptionalFixed } from '@app/helpers/format';
import { AddLiquidityModalV3 } from '@app/screens/positions/components/AddLiquidityModalV3';
import { RemoveLiquidityModalV3 } from '@app/screens/positions/components/RemoveLiquidityModalV3';
import { useNativePriceQuery } from '@app/hooks/v3/useNativePriceQuery';
import { formatUSD } from '@app/helpers/formatUSD';
import { BurnLiquidityModalV3 } from '@app/screens/positions/components/BurnLiquidityModalV3';
import { isOldWethPool } from '@app/helpers/positions';
import { Icon } from '@app/components/Icon';
import { APP_ROUTES } from '@app/constants/routes';
import { useRouter } from 'next/router';
import { getCurrencySymbol } from '@app/helpers/currency';
import { Token } from '@app/types/token';

interface Props extends PropsWithChildren {
  pool: Pool;
  position: FormattedPosition;
  mintInfo: IDerivedMintInfo;
  poolInfo: GraphPool | undefined;
}

export const PositionDetails: FC<Props> = ({
  pool,
  position,
  mintInfo,
  poolInfo
}) => {
  const router = useRouter();
  const [view, setView] = useState(pool.token0.symbol);
  const { data: bundles } = useNativePriceQuery();

  const [tokenA, tokenB] =
    view === pool.token0.symbol
      ? [
          {
            token: pool.token0,
            price: pool.token0Price
          },
          {
            token: pool.token1,
            price: pool.token1Price
          }
        ]
      : [
          { token: pool.token1, price: pool.token1Price },
          { token: pool.token0, price: pool.token0Price }
        ];

  const { token0FeePriceUSD, token1FeePriceUSD } = useMemo(() => {
    if (!bundles || !poolInfo) {
      return {
        token0FeePriceUSD: 0,
        token1FeePriceUSD: 0
      };
    }

    return {
      token0FeePriceUSD:
        Number(position.positionsFees.amount0?.toSignificant()) *
        Number(poolInfo.token0.derivedMatic) *
        Number(bundles.maticPriceUSD),
      token1FeePriceUSD:
        Number(position.positionsFees.amount1?.toSignificant()) *
        Number(poolInfo.token1.derivedMatic) *
        Number(bundles.maticPriceUSD)
    };
  }, [
    bundles,
    poolInfo,
    position.positionsFees.amount0,
    position.positionsFees.amount1
  ]);

  const { token0PriceUSD, token1PriceUSD } = useMemo(() => {
    if (!bundles || !poolInfo) {
      return {
        token0PriceUSD: 0,
        token1PriceUSD: 0
      };
    }

    return {
      token0PriceUSD:
        Number(position.composition.amount0?.toSignificant()) *
        Number(poolInfo.token0.derivedMatic) *
        Number(bundles.maticPriceUSD),
      token1PriceUSD:
        Number(position.composition.amount1?.toSignificant()) *
        Number(poolInfo.token1.derivedMatic) *
        Number(bundles.maticPriceUSD)
    };
  }, [
    poolInfo,
    bundles,
    position.composition.amount0,
    position.composition.amount1
  ]);

  const isOldWethPosition = isOldWethPool(
    poolInfo?.token0.id,
    poolInfo?.token1.id
  );

  function renderRow(label: ReactNode, value: ReactNode) {
    return (
      <HStack justifyContent="space-between" w="100%">
        {label}
        {value}
      </HStack>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        border: '1px solid',
        borderColor: 'neutral.700',
        bg: 'neutral.1000',
        padding: ['8px', '24px'],
        margin: '0 24px 24px',
        borderRadius: '8px'
      }}
    >
      <Grid
        alignItems="flex-start"
        gridGap={8}
        gridTemplateColumns={['1fr', '1.2fr 1fr 1fr']}
      >
        <VStack
          alignItems="flex-start"
          width="100%"
          gap={4}
          bg="gray.2"
          p={[2, 3]}
        >
          <HStack
            bg="gray.2"
            borderRadius={4}
            w="100%"
            justifyContent="space-between"
            // p={2}
          >
            <Text fontSize={16} color="neutral.400">
              Range
            </Text>
            <HStack
              w="fit-content"
              alignItems="flex-start"
              gap="3px"
              px="4px"
              py="2px"
              sx={{
                background: 'neutral.1000',
                border: '1px solid',
                borderColor: 'neutral.700',
                borderRadius: '6px'
              }}
            >
              <Button
                variant="tertiary"
                fontSize={14}
                onClick={() => {
                  setView(pool.token0.symbol);
                }}
                sx={{
                  padding: '4px 8px',
                  color: view === pool.token0.symbol ? 'orange' : 'gray.4',
                  bg: view === pool.token0.symbol ? 'gray.2' : 'transparent'
                }}
                size="md"
              >
                {pool.token0.symbol}
              </Button>
              <Button
                fontSize={14}
                variant="tertiary"
                onClick={() => {
                  setView(pool.token1.symbol);
                }}
                sx={{
                  padding: '4px 8px',
                  color: view === pool.token1.symbol ? 'orange' : 'gray.4',
                  bg: view === pool.token1.symbol ? 'gray.2' : 'transparent'
                }}
                size="md"
              >
                {pool.token1.symbol}
              </Button>
            </HStack>
          </HStack>
          <HStack w="100%">
            <VStack
              gap={1}
              justifyContent="center"
              w="100%"
              backgroundColor="neutral.1000"
              py={2}
            >
              <Text color="neutral.400" fontSize={12}>
                Current price
              </Text>
              <Text color="warning.100" fontWeight={500}>
                {tokenA.price.toFixed()}
              </Text>
              <Text color="neutral.400" fontSize={12}>
                {tokenA.token.symbol} per {tokenB.token.symbol}
              </Text>
            </VStack>
          </HStack>
          <HStack w="100%">
            <VStack
              gap={1}
              justifyContent="center"
              w="100%"
              backgroundColor="neutral.1000"
              py={2}
            >
              <Text fontSize={12} color="neutral.400">
                Min price
              </Text>
              <Text color="neutral.100" fontWeight={500}>
                {mintInfo.ticksAtLimit[Bound.LOWER]
                  ? '0'
                  : position.position.token0PriceLower?.toSignificant(5) ?? ''}
              </Text>
              <Text color="neutral.400" fontSize={12}>
                {pool.token0.symbol} per {pool.token1.symbol}
              </Text>
            </VStack>
            <VStack
              gap={1}
              justifyContent="center"
              w="100%"
              backgroundColor="neutral.1000"
              py={2}
            >
              <Text fontSize={12} color="neutral.400">
                Max price
              </Text>
              <Text color="neutral.100" fontWeight={500}>
                {mintInfo.ticksAtLimit[Bound.UPPER]
                  ? '∞'
                  : position.position.token0PriceUpper?.toSignificant(5) ?? ''}
              </Text>
              <Text color="neutral.400" fontSize={12}>
                {pool.token0.symbol} per {pool.token1.symbol}
              </Text>
            </VStack>
          </HStack>
        </VStack>

        <VStack alignItems="flex-start" width="100%" gap={4} height="100%">
          <HStack
            bg="gray.2"
            borderRadius={4}
            w="100%"
            justifyContent="space-between"
            p={4}
          >
            <Text fontSize={16} color="neutral.400">
              Composition
            </Text>
          </HStack>
          {renderRow(
            <HStack alignItems="center" gap={2}>
              <TokenIcon
                symbol={
                  getCurrencySymbol({
                    contractAddress: pool.token0.address,
                    symbol: pool.token0.symbol
                  } as Token) as string
                }
              />
              <Text fontSize="16px" lineHeight="26px" color="white">
                {
                  getCurrencySymbol({
                    contractAddress: pool.token0.address,
                    symbol: pool.token0.symbol
                  } as Token) as string
                }
              </Text>
            </HStack>,
            <VStack gap={1} alignItems="flex-end">
              <Text fontSize="16px" lineHeight="26px" color="white">
                {toOptionalFixed(
                  +position.composition.amount0.toSignificant(),
                  5
                )}
              </Text>
              <Text fontSize={12} color="neutral.400">
                {formatUSD.format(token0PriceUSD)}
              </Text>
            </VStack>
          )}
          {renderRow(
            <HStack alignItems="center" gap={2}>
              <TokenIcon
                symbol={
                  getCurrencySymbol({
                    contractAddress: pool.token1.address,
                    symbol: pool.token1.symbol
                  } as Token) as string
                }
              />
              <Text fontSize="16px" lineHeight="26px" color="white">
                {
                  getCurrencySymbol({
                    contractAddress: pool.token1.address,
                    symbol: pool.token1.symbol
                  } as Token) as string
                }
              </Text>
            </HStack>,
            <VStack gap={1} alignItems="flex-end">
              <Text fontSize="16px" lineHeight="26px" color="white">
                {toOptionalFixed(
                  +position.composition.amount1.toSignificant(),
                  5
                )}
              </Text>
              <Text fontSize={12} color="neutral.400">
                {formatUSD.format(token1PriceUSD)}
              </Text>
            </VStack>
          )}
          <HStack
            justifyContent="flex-end"
            alignItems="flex-end"
            width="100%"
            height="100%"
          >
            {position?.position.liquidity &&
            +position.position.liquidity.toString() > 0 ? (
              <RemoveLiquidityModalV3 positionId={position.id} fullWidth />
            ) : (
              <BurnLiquidityModalV3
                fullWidth
                position={position.position}
                positionId={position.id}
              />
            )}
            {isOldWethPosition &&
            poolInfo &&
            +position.position.liquidity.toString() > 0 ? (
              <Button
                width="100%"
                variant="rowCompact"
                sx={{
                  background: 'orange',
                  color: 'black'
                }}
                onClick={() => {
                  router.push(
                    APP_ROUTES.transfer(
                      position.id,
                      poolInfo.token0.id,
                      poolInfo.token1.id
                    )
                  );
                }}
              >
                Transfer
              </Button>
            ) : (
              <AddLiquidityModalV3
                fullWidth
                mintInfo={mintInfo}
                position={position.position}
                positionId={position.id}
              />
            )}
          </HStack>
        </VStack>

        <VStack alignItems="flex-start" width="100%" gap={4} height="100%">
          <HStack
            bg="gray.2"
            borderRadius={4}
            w="100%"
            justifyContent="space-between"
            p={4}
          >
            <Text fontSize={16} color="neutral.400">
              Fees
            </Text>
          </HStack>
          {renderRow(
            <HStack alignItems="center" gap={2}>
              <TokenIcon
                symbol={
                  getCurrencySymbol({
                    contractAddress: pool.token0.address,
                    symbol: pool.token0.symbol
                  } as Token) as string
                }
              />
              <Text fontSize="16px" lineHeight="26px" color="white">
                {
                  getCurrencySymbol({
                    contractAddress: pool.token0.address,
                    symbol: pool.token0.symbol
                  } as Token) as string
                }
              </Text>
            </HStack>,
            <VStack gap={1} alignItems="flex-end">
              <Text fontSize={14} lineHeight="26px">
                {position.positionsFees.amount0
                  ? `${toOptionalFixed(
                      +position.positionsFees.amount0.toSignificant(),
                      6
                    )}`
                  : 'n/a'}
              </Text>
              <Text fontSize={12} color="neutral.400">
                {formatUSD.format(token0FeePriceUSD)}
              </Text>
            </VStack>
          )}
          {renderRow(
            <HStack alignItems="center" gap={2}>
              <TokenIcon
                symbol={
                  getCurrencySymbol({
                    contractAddress: pool.token1.address,
                    symbol: pool.token1.symbol
                  } as Token) as string
                }
              />
              <Text fontSize="16px" lineHeight="26px" color="white">
                {
                  getCurrencySymbol({
                    contractAddress: pool.token1.address,
                    symbol: pool.token1.symbol
                  } as Token) as string
                }
              </Text>
            </HStack>,
            <VStack gap={1} alignItems="flex-end">
              <Text fontSize={14} lineHeight="26px">
                {position.positionsFees.amount1
                  ? `${toOptionalFixed(
                      +position.positionsFees.amount1.toSignificant(),
                      6
                    )}`
                  : 'n/a'}
              </Text>
              <Text fontSize={12} color="neutral.400">
                {formatUSD.format(token1FeePriceUSD)}
              </Text>
            </VStack>
          )}

          {!!position.feesUSD && (
            <HStack
              justifyContent="flex-end"
              alignItems="flex-end"
              width="100%"
              height="100%"
            >
              <CollectFeesModalV3
                positionFeesUSD={position.feesUSD}
                mintInfo={mintInfo}
                positionId={position.id}
                fullWidth
              />
            </HStack>
          )}
        </VStack>
      </Grid>
    </Box>
  );
};
